import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Generic per-list reorder helper used by the up/down/top/bottom move actions
 * on the Active page. It computes the new task order for the task's own list
 * (preserving cross-list order in the rest of activeTasks), updates state
 * optimistically, and persists via POST /api/tasks/reorder.
 *
 * @param {object} args
 * @param {string} args.taskId         – id of the task to move
 * @param {Array}  args.activeTasks    – current activeTasks array
 * @param {Function} args.setActiveTasks – setter for activeTasks
 * @param {string} args.successMessage – toast text on success
 * @param {(listTasks: Array, currentIndex: number) => Array | null} args.computeNewOrder
 *        Function that receives the tasks of the moved task's own list (in
 *        their current visual order) and the dragged task's index in that
 *        list, and returns the new ordering — or null if no move needed.
 */
export async function reorderWithinList({
  taskId,
  activeTasks,
  setActiveTasks,
  successMessage,
  computeNewOrder,
}) {
  const taskToMove = activeTasks.find((t) => t.id === taskId);
  if (!taskToMove) return;

  const listId = taskToMove.active_list_id || null;
  const listTasks = activeTasks.filter(
    (t) => (t.active_list_id || null) === listId
  );
  const currentIndex = listTasks.findIndex((t) => t.id === taskId);
  if (currentIndex < 0) return;

  const newOrder = computeNewOrder(listTasks, currentIndex);
  if (!newOrder) return;

  // Replace the slice belonging to this list with the new order
  const ids = newOrder.map((t) => t.id);
  const filtered = activeTasks.filter((t) => !ids.includes(t.id));
  setActiveTasks([...filtered, ...newOrder]);

  try {
    await axios.post(
      `${API}/tasks/reorder`,
      { task_ids: newOrder.map((t) => t.id) },
      { withCredentials: true }
    );
    if (successMessage) toast.success(successMessage);
  } catch (error) {
    console.error('Error reordering within list:', error);
    toast.error('Failed to move task');
  }
}
